import { showConfirmDialog } from "../components/ConfirmDialog";

/**
 * Loại dữ liệu khi xóa — dùng với confirmDelete().
 * Mỗi loại có tiêu đề và danh sách hệ quả riêng để người dùng hiểu rõ trước khi xóa.
 */
export const DELETE_ENTITY = {
  generic: "generic",
  customer: "customer",
  customer_school_with_classes: "customer_school_with_classes",
  class: "class",
  student: "student",
  student_bulk: "student_bulk",
  course: "course",
  subject: "subject",
  lesson: "lesson",
  exam_result: "exam_result",
  exam_result_bulk: "exam_result_bulk",
  document: "document",
  certificate_template: "certificate_template",
  contract: "contract",
  employee: "employee",
  teacher_account: "teacher_account",
  teacher_device: "teacher_device",
  customer_tag: "customer_tag",
  contact_history: "contact_history",
  notification: "notification",
  role: "role",
  permission: "permission",
  task_waiting_day: "task_waiting_day",
  task_report: "task_report",
  plan_draft: "plan_draft",
  plan_detail_row: "plan_detail_row",
  receipt: "receipt",
  business_trip: "business_trip",
  advance_request: "advance_request",
  user_role: "user_role",
};

const IRREVERSIBLE_FOOTER =
  "Thao tác này không thể hoàn tác. Bạn có chắc muốn tiếp tục?";

function buildDeleteText({ intro, consequences = [], extraLines = [], footer = IRREVERSIBLE_FOOTER }) {
  const lines = [];
  if (intro) lines.push(intro);
  if (consequences.length) {
    if (lines.length) lines.push("");
    lines.push("Hệ quả khi xóa:");
    consequences.forEach((line) => lines.push(`• ${line}`));
  }
  if (extraLines.length) {
    if (lines.length) lines.push("");
    extraLines.forEach((line) => lines.push(line));
  }
  if (footer) {
    if (lines.length) lines.push("");
    lines.push(footer);
  }
  return lines.join("\n");
}

const PRESETS = {
  [DELETE_ENTITY.generic]: {
    title: "Xác nhận xóa",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa "${name}".` : "Bạn sắp xóa dữ liệu này.",
    consequences: ["Dữ liệu sẽ bị xóa vĩnh viễn khỏi hệ thống."],
  },
  [DELETE_ENTITY.customer]: {
    title: "Xác nhận xóa khách hàng",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa khách hàng "${name}".` : "Bạn sắp xóa khách hàng này.",
    consequences: [
      "Hồ sơ khách hàng và thông tin CRM liên quan sẽ bị xóa.",
      "Hợp đồng, lịch sử liên hệ, tag gắn với khách hàng cũng bị xóa theo.",
    ],
  },
  [DELETE_ENTITY.customer_school_with_classes]: {
    title: "Xác nhận xóa trường học",
    buildIntro: ({ name, context }) =>
      `Trường "${name || "này"}" đang gắn với ${context.linkedClassCount || 0} lớp học trên hệ thống.`,
    consequences: [
      "Hồ sơ trường và dữ liệu CRM liên quan sẽ bị xóa.",
      "Các lớp học vẫn được giữ trong hệ thống.",
      "Các lớp sẽ không còn hiển thị trường đối tác (mất liên kết với trường vừa xóa).",
    ],
    confirmText: "Vẫn xóa",
  },
  [DELETE_ENTITY.class]: {
    title: "Xác nhận xóa lớp học",
    buildIntro: ({ name, context }) => {
      const teacher = context.teacherName ? ` — GV: ${context.teacherName}` : "";
      const count = context.studentCount;
      const countNote =
        count != null && count > 0
          ? ` Lớp đang có ${count} học viên đăng ký.`
          : "";
      return `Bạn sắp xóa lớp "${name || "này"}"${teacher}.${countNote}`;
    },
    consequences: [
      "Xóa điểm danh, bảng điểm, đánh giá/kết quả và yêu cầu cấp chứng nhận của lớp này.",
      "Gỡ liên kết học viên với lớp (bản ghi đăng ký lớp bị xóa).",
      "Các hồ sơ học viên không còn thuộc lớp nào khác cũng sẽ bị xóa khỏi hệ thống.",
    ],
  },
  [DELETE_ENTITY.student]: {
    title: "Xác nhận xóa học viên",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa hồ sơ học viên "${name}".` : "Bạn sắp xóa hồ sơ học viên này.",
    consequences: [
      "Xóa vĩnh viễn hồ sơ học viên trong hệ thống.",
      "Xóa điểm danh, đăng ký lớp và bảng điểm gắn với học viên này.",
    ],
  },
  [DELETE_ENTITY.student_bulk]: {
    title: "Xác nhận xóa hàng loạt",
    buildIntro: ({ context }) =>
      `Bạn sắp xóa ${context.count || 0} hồ sơ học viên đã chọn.`,
    consequences: [
      "Xóa vĩnh viễn toàn bộ hồ sơ học viên đã chọn.",
      "Xóa điểm danh, đăng ký lớp và bảng điểm liên quan từng học viên.",
    ],
  },
  [DELETE_ENTITY.course]: {
    title: "Xác nhận xóa khóa học",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa khóa học "${name}".` : "Bạn sắp xóa khóa học này.",
    consequences: [
      "Dữ liệu khóa học và cấu hình liên quan có thể bị ảnh hưởng.",
      "Nếu còn lớp đang dùng khóa này, thao tác có thể thất bại — hãy kiểm tra trước khi xóa.",
    ],
  },
  [DELETE_ENTITY.subject]: {
    title: "Xác nhận xóa môn học",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa môn học "${name}".` : "Bạn sắp xóa môn học này.",
    consequences: ["Môn học và dữ liệu gắn với môn sẽ bị xóa khỏi hệ thống."],
  },
  [DELETE_ENTITY.lesson]: {
    title: "Xác nhận xóa bài học",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa bài học "${name}".` : "Bạn sắp xóa bài học này.",
    consequences: [
      "Bài học sẽ bị xóa vĩnh viễn.",
      "Liên kết tài liệu với bài học sẽ bị gỡ; file tài liệu trên hệ thống không tự xóa.",
    ],
  },
  [DELETE_ENTITY.exam_result]: {
    title: "Xác nhận xóa bản ghi điểm",
    buildIntro: ({ name, context }) =>
      name
        ? `Bạn sắp xóa bản ghi điểm của "${name}".`
        : "Bạn sắp xóa bản ghi điểm này.",
    consequences: [
      "Bản ghi điểm và dữ liệu đánh giá/kết quả liên quan (nếu có) sẽ bị xóa.",
    ],
  },
  [DELETE_ENTITY.exam_result_bulk]: {
    title: "Xác nhận xóa hàng loạt bản ghi điểm",
    buildIntro: ({ context }) =>
      `Bạn sắp xóa ${context.count || 0} bản ghi điểm đã chọn trên trang này.`,
    consequences: [
      "Xóa vĩnh viễn toàn bộ bản ghi điểm đã chọn.",
      "Dữ liệu đánh giá/kết quả liên quan (nếu có) cũng bị xóa theo.",
    ],
  },
  [DELETE_ENTITY.document]: {
    title: "Xác nhận xóa tài liệu",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa tài liệu "${name}".` : "Bạn sắp xóa tài liệu này.",
    consequences: ["File tài liệu sẽ bị xóa vĩnh viễn khỏi hệ thống."],
  },
  [DELETE_ENTITY.certificate_template]: {
    title: "Xác nhận vô hiệu hóa biểu mẫu",
    buildIntro: ({ name }) =>
      name
        ? `Bạn sắp vô hiệu hóa biểu mẫu "${name}".`
        : "Bạn sắp vô hiệu hóa biểu mẫu này.",
    consequences: [
      "Biểu mẫu sẽ không còn dùng cho cấp chứng nhận mới.",
      "Các chứng nhận đã cấp trước đó không bị thay đổi.",
    ],
    confirmText: "Vô hiệu hóa",
  },
  [DELETE_ENTITY.contract]: {
    title: "Xác nhận xóa hợp đồng",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa hợp đồng "${name}".` : "Bạn sắp xóa hợp đồng này.",
    consequences: ["Hợp đồng và dữ liệu đính kèm sẽ bị xóa vĩnh viễn."],
  },
  [DELETE_ENTITY.employee]: {
    title: "Xác nhận xóa nhân viên",
    buildIntro: ({ name, context }) => {
      const code = context.code ? ` (mã ${context.code})` : "";
      return name
        ? `Bạn sắp xóa hồ sơ nhân viên "${name}"${code}.`
        : "Bạn sắp xóa hồ sơ nhân viên này.";
    },
    consequences: ["Hồ sơ nhân sự và dữ liệu gắn với nhân viên có thể bị ảnh hưởng."],
  },
  [DELETE_ENTITY.teacher_account]: {
    title: "Xác nhận xóa tài khoản giáo viên",
    buildIntro: ({ name }) =>
      name
        ? `Bạn sắp xóa tài khoản giáo viên "${name}".`
        : "Bạn sắp xóa tài khoản giáo viên này.",
    consequences: [
      "Tài khoản đăng nhập giáo viên sẽ bị xóa.",
      "Giáo viên không thể đăng nhập lại bằng tài khoản này.",
    ],
  },
  [DELETE_ENTITY.teacher_device]: {
    title: "Xác nhận gỡ thiết bị",
    buildIntro: () => "Bạn sắp gỡ thiết bị đăng nhập của giáo viên.",
    consequences: ["Thiết bị sẽ bị đăng xuất và cần đăng nhập lại trên thiết bị mới."],
    confirmText: "Gỡ thiết bị",
  },
  [DELETE_ENTITY.customer_tag]: {
    title: "Xác nhận xóa tag",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa tag "${name}".` : "Bạn sắp xóa tag này.",
    consequences: ["Tag sẽ bị gỡ khỏi mọi khách hàng đang gắn tag này."],
  },
  [DELETE_ENTITY.contact_history]: {
    title: "Xác nhận xóa lịch sử liên hệ",
    buildIntro: () => "Bạn sắp xóa bản ghi lịch sử liên hệ.",
    consequences: ["Nội dung tương tác với khách hàng sẽ bị xóa vĩnh viễn."],
  },
  [DELETE_ENTITY.notification]: {
    title: "Xác nhận xóa thông báo",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa thông báo "${name}".` : "Bạn sắp xóa thông báo này.",
    consequences: ["Thông báo sẽ không còn hiển thị với người dùng."],
  },
  [DELETE_ENTITY.role]: {
    title: "Xác nhận xóa vai trò",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa vai trò "${name}".` : "Bạn sắp xóa vai trò này.",
    consequences: [
      "Vai trò và phân quyền gắn với vai trò sẽ bị xóa.",
      "Người dùng đang được gán vai trò này có thể mất quyền truy cập.",
    ],
  },
  [DELETE_ENTITY.permission]: {
    title: "Xác nhận xóa quyền",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa quyền "${name}".` : "Bạn sắp xóa quyền này.",
    consequences: ["Quyền sẽ bị gỡ khỏi các vai trò đang sử dụng."],
  },
  [DELETE_ENTITY.task_waiting_day]: {
    title: "Xác nhận xóa công việc",
    buildIntro: ({ context }) =>
      `Bạn sắp xóa toàn bộ công việc "Chờ duyệt" ngày ${context.workDayLabel || ""}.`.trim(),
    consequences: ["Mọi mục công việc chờ duyệt trong ngày đó sẽ bị xóa."],
  },
  [DELETE_ENTITY.task_report]: {
    title: "Xác nhận xóa báo cáo",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa báo cáo "${name}".` : "Bạn sắp xóa báo cáo này.",
    consequences: ["Báo cáo và nội dung đính kèm sẽ bị xóa vĩnh viễn."],
  },
  [DELETE_ENTITY.plan_draft]: {
    title: "Xác nhận xóa kế hoạch nháp",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa kế hoạch nháp "${name}".` : "Bạn sắp xóa kế hoạch nháp này.",
    consequences: ["Toàn bộ nội dung kế hoạch nháp sẽ bị xóa."],
  },
  [DELETE_ENTITY.plan_detail_row]: {
    title: "Xác nhận xóa dòng kế hoạch",
    buildIntro: () => "Bạn sắp xóa dòng chi tiết trong kế hoạch.",
    consequences: ["Dòng này sẽ bị xóa khỏi kế hoạch đang soạn."],
  },
  [DELETE_ENTITY.receipt]: {
    title: "Xác nhận xóa phiếu thu",
    buildIntro: ({ name }) =>
      name ? `Bạn sắp xóa phiếu thu "${name}".` : "Bạn sắp xóa phiếu thu này.",
    consequences: ["Phiếu thu và dữ liệu liên quan sẽ bị xóa vĩnh viễn."],
  },
  [DELETE_ENTITY.business_trip]: {
    title: "Xác nhận xóa phiếu công tác",
    buildIntro: ({ name, context }) => {
      const who = context.employeeName ? ` của ${context.employeeName}` : "";
      return name
        ? `Bạn sắp xóa phiếu công tác "${name}"${who}.`
        : `Bạn sắp xóa phiếu công tác${who}.`;
    },
    consequences: ["Phiếu công tác và tiến trình duyệt liên quan sẽ bị xóa."],
  },
  [DELETE_ENTITY.user_role]: {
    title: "Xác nhận gỡ phân quyền",
    buildIntro: ({ name }) =>
      name
        ? `Bạn sắp gỡ vai trò/phân quyền của nhân viên "${name}".`
        : "Bạn sắp gỡ phân quyền nhân viên này.",
    consequences: ["Nhân viên có thể mất quyền truy cập các chức năng tương ứng."],
    confirmText: "Gỡ phân quyền",
  },
  [DELETE_ENTITY.advance_request]: {
    title: "Xác nhận xóa đề nghị tạm ứng",
    buildIntro: ({ name, context }) => {
      const amt = context.amount
        ? ` (${Number(context.amount).toLocaleString("vi-VN")} ₫)`
        : "";
      return name
        ? `Bạn sắp xóa đề nghị tạm ứng${amt} — "${name}".`
        : `Bạn sắp xóa đề nghị tạm ứng${amt}.`;
    },
    consequences: ["Đề nghị và lịch sử duyệt liên quan sẽ bị xóa."],
  },
};

/**
 * Hiển thị hộp thoại xác nhận xóa với mô tả hệ quả rõ ràng.
 *
 * @param {Object} options
 * @param {string} options.type - Một giá trị DELETE_ENTITY
 * @param {string} [options.name] - Tên hiển thị (khách hàng, lớp, file…)
 * @param {Object} [options.context] - Số liệu bổ sung (linkedClassCount, studentCount…)
 * @param {string[]} [options.extraLines] - Dòng bổ sung
 * @param {string} [options.confirmText]
 * @param {string} [options.cancelText]
 * @param {number} [options.confirmDelaySeconds]
 */
export async function confirmDelete({
  type = DELETE_ENTITY.generic,
  name,
  context = {},
  extraLines = [],
  confirmText,
  cancelText = "Huỷ",
  confirmDelaySeconds = 0,
}) {
  const preset = PRESETS[type] || PRESETS[DELETE_ENTITY.generic];
  const intro = preset.buildIntro
    ? preset.buildIntro({ name, context })
    : name
      ? `Bạn sắp xóa "${name}".`
      : "Bạn sắp xóa dữ liệu này.";
  const text = buildDeleteText({
    intro,
    consequences: preset.consequences,
    extraLines,
  });

  return showConfirmDialog({
    title: preset.title,
    text,
    confirmText: confirmText ?? preset.confirmText ?? "Vẫn xóa",
    cancelText,
    confirmDelaySeconds,
  });
}
